/**
 * AppliedFilter Component
 * @module views/components/AppliedFilter
 */
import './AppliedFilter.scss';

import React from 'react';

import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Text from '@ulta/core/components/Text/Text';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { useFiltersColumnContext } from '@ulta/providers/FiltersColumnProvider/FiltersColumnProvider';

import { FILTER_GROUPS } from '@ulta/utils/constants/filters';

import ReviewStarsCard from '../ReviewStarsCard/ReviewStarsCard';
import Swatch, { reduceSwatchProps } from '../Swatch/Swatch';

const colorImageDimensions = {
  height:18,
  width:18
};

/**
 * Represents to show rating or swatch component
 * @method
 * @param {AppliedFilterContentProps} props - React properties passed from composition
 * @returns ratings or swatch
 */
const AppliedFilterContent = ( props ) => {
  const { colorCode, colorImage, rating, itemText = defaultProps.itemText, groupType } = props;
  const swatchProps = reduceSwatchProps( { colorCode, colorImage, colorImageDimensions } );
  if( groupType === FILTER_GROUPS.RATING ){
    return (
      <div className='AppliedFilter__reviews'>
        <ReviewStarsCard rating={ rating } />
      </div>
    );
  }
  return (
    <div className='AppliedFilter__buttonText'>
      { swatchProps && <Swatch { ...spreadProps( { ...swatchProps } ) } /> }
      <Text htmlTag='span'
        textStyle='body-2'
      >
        { itemText }
      </Text>
    </div>
  );
};

/**
 * Represents a Filter component
 * @method
 * @param {AppliedFilterProps} props - React properties passed from composition
 * @returns AppliedFilter
 */
const AppliedFilter = ( props ) => {
  const { ariaLabel = defaultProps.ariaLabel, colorCode, colorImage, itemText, rating, groupType } = props;
  const { loading } = useFiltersColumnContext();

  return (
    // TODO: This needs to be replaced with DismissiblePill component in future
    <div className='AppliedFilter'>
      <AppliedFilterContent
        itemText={ itemText }
        rating={ rating }
        colorCode={ colorCode }
        colorImage={ colorImage }
        groupType={ groupType }
      />

      <Button
        className='AppliedFilter__button'
        secondary
        ariaLabel={ ariaLabel }
        icon={ true }
        iconImage={ 'X' }
        iconSize={ 's' }
        disabled={ loading }
        onClick={ props.onClick }
        ariaHiddenIcon={ true }
      />
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} ariaLabel='' - Default ariaLabel
 * @property {string} itemText='' - Default itemText
 * @property {function} onClick=null - Default onClick
 */
export const defaultProps = {
  ariaLabel: '',
  itemText: '',
  onClick: null
};

/**
 * Property type definitions
 * @typedef AppliedFilterProps
 * @type {object}
 * @property {string} ariaLabel - Text for the screen readers
 * @property {string} itemText - Label for filter
 * @property {function} onClick - Function as the event handler
 * @property {string} colorCode - color code for Swatch used in color filter
 * @property {object} colorImage - UltaAsset for color swatch
 */
export const propTypes = {
  /** Text for the screen readers */
  ariaLabel: PropTypes.string,
  /** Text for filter label */
  itemText: PropTypes.string,
  /** Function as the event handler */
  onClick: PropTypes.func,
  /** Set color code for Swatch div */
  colorCode: PropTypes.string,
  /** Set multiColorImage code for Swatch div */
  colorImage: PropTypes.object
};

AppliedFilter.propTypes = propTypes;
AppliedFilter.defaultProps = defaultProps;

export default AppliedFilter;
